/**
 * ARGOV-EVENTS-CONNECT-01 Phase 2
 * ConferenceRoom — Native LiveKit-powered video conference room.
 *
 * Features: participant grid, mic/camera/screen controls, waiting room panel (host),
 * active-speaker indication, responsive mobile/desktop layout, ARGOV branding.
 *
 * Behaviour:
 *   - Fetches a short-lived join token from the backend on mount
 *   - Connects to LiveKit Cloud via @livekit/components-react
 *   - Hosts see a WaitingRoom panel showing participants needing admission
 *   - Guests (joined with can_publish=false) see a waiting screen until admitted
 *   - All moderation calls go through the authenticated backend (secrets never in client)
 */
import "@livekit/components-styles";
import {
  LiveKitRoom,
  VideoConference,
  useConnectionState,
  useLocalParticipant,
  useRemoteParticipants,
  RoomAudioRenderer,
} from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Video, Mic, Users, LogOut, Clock, AlertTriangle, RefreshCw, UserCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ── LIVEKIT AVAILABILITY CHECK ─────────────────────────────────────────────────
function isLiveKitAvailable(serverUrl, token) {
  if (!serverUrl || !token) return false;
  if (serverUrl.startsWith("wss://mock.")) return false;  // mock mode
  if (token.startsWith("mock-")) return false;            // mock token
  return true;
}

// ── WAITING ROOM PANEL (host only) ────────────────────────────────────────────
function WaitingRoomPanel({ mid, isHost, onAdmit, onRemove }) {
  const [waiting, setWaiting]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const remoteParticipants = useRemoteParticipants();

  // Poll waiting participants from backend (every 5 s)
  const fetchWaiting = useCallback(async () => {
    if (!isHost) return;
    try {
      const r = await fetch(`${API}/api/connect/meetings/${mid}/conference/state`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setWaiting(d.waiting || []);
      }
    } catch {/* silent */}
  }, [mid, isHost]);

  useEffect(() => {
    fetchWaiting();
    const t = setInterval(fetchWaiting, 5000);
    return () => clearInterval(t);
  }, [fetchWaiting]);

  const admit = async (identity) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/connect/meetings/${mid}/conference/participants/${identity}/admit`, {
        method: "POST", credentials: "include",
      });
      if (r.ok) { toast.success("Participant admitted"); fetchWaiting(); onAdmit?.(); }
      else toast.error("Could not admit participant");
    } catch { toast.error("Network error"); }
    finally { setLoading(false); }
  };

  const remove = async (identity) => {
    if (!window.confirm("Remove this participant from the meeting?")) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/connect/meetings/${mid}/conference/participants/${identity}`, {
        method: "DELETE", credentials: "include",
      });
      if (r.ok) { toast.success("Participant removed"); onRemove?.(); }
      else toast.error("Could not remove participant");
    } catch { toast.error("Network error"); }
    finally { setLoading(false); }
  };

  const allParticipants = remoteParticipants.map(p => ({
    identity: p.identity,
    name: p.name || p.identity,
    canPublish: p.permissions?.canPublish ?? true,
  }));

  const waitingList = allParticipants.filter(p => !p.canPublish);
  const activeList  = allParticipants.filter(p => p.canPublish);

  return (
    <div className="flex flex-col h-full bg-slate-900 border-l border-slate-700 w-72 flex-shrink-0" data-testid="conference-waiting-panel">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-white">Participants</span>
          <Badge className="ml-auto text-xs bg-slate-700 text-slate-300">{activeList.length + waitingList.length + 1}</Badge>
        </div>
      </div>

      {waitingList.length > 0 && (
        <div className="p-3 bg-amber-900/30 border-b border-amber-800/40">
          <p className="text-xs font-semibold text-amber-400 mb-2 uppercase tracking-wide">Waiting Room ({waitingList.length})</p>
          <div className="space-y-2">
            {waitingList.map(p => (
              <div key={p.identity} className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-amber-700/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-amber-300 font-bold">{(p.name || p.identity)[0].toUpperCase()}</span>
                </div>
                <span className="flex-1 text-xs text-amber-200 truncate">{p.name || p.identity}</span>
                <Button
                  size="sm"
                  className="h-6 text-xs px-2 bg-green-700 hover:bg-green-600 text-white"
                  data-testid={`conference-admit-${p.identity}`}
                  onClick={() => admit(p.identity)}
                  disabled={loading}
                ><UserCheck className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">In Meeting ({activeList.length + 1})</p>
        {/* Self */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center">
            <span className="text-xs text-white font-bold">H</span>
          </div>
          <span className="text-xs text-slate-300">You (Host)</span>
          <Badge className="ml-auto text-xs bg-indigo-900 text-indigo-300">Host</Badge>
        </div>
        {activeList.map(p => (
          <div key={p.identity} className="flex items-center gap-2 group">
            <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center">
              <span className="text-xs text-slate-300 font-bold">{(p.name || p.identity)[0].toUpperCase()}</span>
            </div>
            <span className="flex-1 text-xs text-slate-300 truncate">{p.name || p.identity}</span>
            <button
              className="opacity-0 group-hover:opacity-100 text-xs text-red-400 hover:text-red-300"
              data-testid={`conference-remove-${p.identity}`}
              onClick={() => remove(p.identity)}
            >✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── WAITING SCREEN (guest before admission) ───────────────────────────────────
function WaitingScreen({ displayName }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-slate-900 text-center p-8" data-testid="conference-waiting-screen">
      <div className="w-20 h-20 rounded-full bg-amber-600/20 border-2 border-amber-500/40 flex items-center justify-center mb-6 animate-pulse">
        <Clock className="w-8 h-8 text-amber-400" />
      </div>
      <h2 className="text-2xl font-semibold text-white mb-2">You're in the waiting room</h2>
      <p className="text-slate-400 max-w-sm">
        <span className="font-medium text-white">{displayName}</span>, please wait while the host reviews your request to join.
      </p>
      <p className="text-slate-500 text-sm mt-4">This page will update automatically when you're admitted.</p>
    </div>
  );
}

// ── MOCK MODE NOTICE ───────────────────────────────────────────────────────────
function MockModeNotice({ serverUrl, onLeave }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-slate-900 text-center p-8" data-testid="conference-mock-notice">
      <div className="w-16 h-16 rounded-full bg-blue-600/20 border-2 border-blue-500/40 flex items-center justify-center mb-6">
        <Video className="w-7 h-7 text-blue-400" />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">ARGOV Native Conference</h2>
      <div className="bg-amber-900/30 border border-amber-700/40 rounded-lg p-4 max-w-md text-left mb-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-amber-300">LiveKit Credentials Required</span>
        </div>
        <p className="text-sm text-amber-200/80">
          The platform is running in mock mode because LiveKit credentials are not yet configured.
          Add <code className="text-amber-300 bg-amber-900/50 px-1 rounded">LIVEKIT_URL</code>,{" "}
          <code className="text-amber-300 bg-amber-900/50 px-1 rounded">LIVEKIT_API_KEY</code>,{" "}
          and <code className="text-amber-300 bg-amber-900/50 px-1 rounded">LIVEKIT_API_SECRET</code>{" "}
          to your backend environment to enable real media sessions.
        </p>
        <p className="text-xs text-slate-400 mt-2">
          Your LiveKit Cloud account: <a href="https://cloud.livekit.io" target="_blank" rel="noreferrer" className="underline text-blue-400">cloud.livekit.io</a>
        </p>
      </div>
      <p className="text-slate-500 text-xs mb-6 max-w-xs">
        All other meeting management features (scheduling, outcomes, invitations) work independently of LiveKit.
      </p>
      <Button onClick={onLeave} variant="outline" className="text-slate-300 border-slate-600 hover:bg-slate-700">
        <LogOut className="w-4 h-4 mr-2" />
        Back to Meeting
      </Button>
    </div>
  );
}

// ── ROOM CONTENT (when connected) ─────────────────────────────────────────────
function RoomContent({ mid, isHost, displayName }) {
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const isWaiting = !localParticipant?.permissions?.canPublish;

  if (connectionState === ConnectionState.Connecting) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-900" data-testid="conference-connecting">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-indigo-400 mx-auto mb-3 animate-spin" />
          <p className="text-slate-400">Connecting to conference…</p>
        </div>
      </div>
    );
  }

  if (!isHost && isWaiting) {
    return <WaitingScreen displayName={displayName} />;
  }

  return (
    <div className="flex-1 flex overflow-hidden" data-testid="conference-room-active">
      <div className="flex-1 overflow-hidden">
        <VideoConference />
      </div>
      {isHost && (
        <WaitingRoomPanel mid={mid} isHost={isHost} />
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function ConferenceRoom() {
  const { mid } = useParams();
  const navigate = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [conf,       setConf]       = useState(null);    // {server_url, token, is_host, title}

  useEffect(() => {
    if (!mid) return;
    setLoading(true);
    fetch(`${API}/api/connect/meetings/${mid}/conference/join`, {
      method: "POST", credentials: "include",
    })
      .then(r => {
        if (!r.ok) return r.json().then(d => Promise.reject(d));
        return r.json();
      })
      .then(d => {
        setConf(d);
        setLoading(false);
      })
      .catch(err => {
        const msg = err?.detail?.message || err?.detail || "Failed to join conference";
        setError(msg);
        setLoading(false);
      });
  }, [mid]);

  const handleLeave = () => navigate(`/connect/meetings/${mid}`);

  if (loading) return (
    <div className="h-screen bg-slate-900 flex items-center justify-center" data-testid="conference-loading">
      <div className="text-center">
        <RefreshCw className="w-8 h-8 text-indigo-400 mx-auto animate-spin mb-3" />
        <p className="text-slate-400">Joining conference…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="h-screen bg-slate-900 flex items-center justify-center p-8" data-testid="conference-error">
      <div className="text-center max-w-sm">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
        <h2 className="text-white text-xl font-semibold mb-2">Cannot Join Conference</h2>
        <p className="text-slate-400 mb-6">{typeof error === "string" ? error : JSON.stringify(error)}</p>
        <Button onClick={handleLeave} variant="outline" className="text-slate-300 border-slate-600 hover:bg-slate-700">
          <LogOut className="w-4 h-4 mr-2" />
          Back to Meeting
        </Button>
      </div>
    </div>
  );

  const isMock = !isLiveKitAvailable(conf?.server_url, conf?.token);

  return (
    <div className="h-screen flex flex-col bg-slate-950" data-testid="conference-room-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Video className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white truncate max-w-xs">{conf?.title || "ARGOV Native Meeting"}</p>
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${isMock ? "bg-amber-400" : "bg-green-400"}`} />
              <span className="text-xs text-slate-400">{isMock ? "Mock mode" : "Live"}</span>
              {conf?.is_host && (
                <Badge className="text-xs bg-indigo-900/60 text-indigo-300 ml-1">Host</Badge>
              )}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
          data-testid="conference-leave-btn"
          onClick={handleLeave}
        >
          <LogOut className="w-4 h-4 mr-1.5" />
          Leave
        </Button>
      </div>

      {/* Room body */}
      <div className="flex-1 flex overflow-hidden">
        {isMock ? (
          <MockModeNotice serverUrl={conf?.server_url} onLeave={handleLeave} />
        ) : (
          <LiveKitRoom
            serverUrl={conf.server_url}
            token={conf.token}
            connect={true}
            audio={true}
            video={true}
            data-lk-theme="default"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <RoomAudioRenderer />
            <RoomContent
              mid={mid}
              isHost={conf?.is_host}
              displayName={conf?.identity}
            />
          </LiveKitRoom>
        )}
      </div>
    </div>
  );
}
